import { type ReactElement } from "react"

import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useQuery } from "react-query"
import { useParams, useSearchParams } from "react-router-dom"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import axios from "axios"
import { useFormikContext } from "formik"

import { SelectionCard, SelectionLoaderCard } from "../../components/cards/SelectionCard"
import { KonanAPI } from "../../services/KonanAPI"
import type { DataFile } from "../../types/generated/api/DataFile"
import type { DataFileVersionsList } from "../../types/generated/api/DataFileVersionsList"
import type { PaginatedDataFileVersionsListList } from "../../types/generated/api/PaginatedDataFileVersionsListList"
import { extractPageFromBackEndPaginationLink } from "../../utils/genericHelpers"
import { parseFileContent } from "../DataFiles/hooks/Queries"
import { FILE_SIZE_LIMIT_IN_BYTES } from "./Constants"
import { formateJsonIntoTableData } from "./Helpers"
import type { DataFileFormik } from "./Interfaces"

export function FileHistory(): ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  const [searchParams] = useSearchParams()

  const dataFileId = searchParams.get("file_id")

  const { values, setFieldValue, setValues } = useFormikContext<DataFileFormik>()

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastDataFileVersionRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isVersionsLoading && !isVersionsFetching && !isFetchingNextPage && fetchNextPage()
    },
  })

  // Fetching metadata selectively
  const { refetch: refetchDataFile } = useQuery<AxiosResponse<DataFile>, AxiosError>(
    ["data-file-version"],
    () => KonanAPI.fetchDataFileVersion(projectId as string, dataFileId as string, values.version as string),
    {
      enabled: false,
      onSuccess: ({ data }) => {
        setFieldValue("isDataFileVersionFetching", true)
        axios
          .get(data?.data_file, {
            headers: {
              Range: `bytes=0-${FILE_SIZE_LIMIT_IN_BYTES}`,
            },
          })
          .then(({ data: file }) => {
            const fileContent = parseFileContent({
              data: file,
              formateJsonIntoTableData,
              setErrorMsg: (msg: string) => {
                NotificationUtils.toast(msg, { severity: "error" })
              },
              tab: "table",
              fileExtension: "csv",
            })

            setValues({
              ...values,
              createdAt: data.created_at,
              createdBy: data.created_by,
              fileUrl: data.data_file,
              fileContent,
              fileName: data.name,
              isDataFileVersionFetching: false,
            })
          })
      },
    },
  )

  const {
    data: dataFileVersions,
    isLoading: isVersionsLoading,
    isFetching: isVersionsFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedDataFileVersionsListList>, AxiosError, DataFileVersionsList>(
    ["data-file-versions", dataFileId],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchDataFileVersions({
        project_uuid: projectId as string,
        data_file_uuid: dataFileId as string,
        page: pageParam,
        pageSize: 20,
      }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce(
          (accumulator, page) => accumulator.concat(page.data.results as Array<DataFileVersionsList>),
          [] as Array<DataFileVersionsList>,
        ),
      }),
      enabled: !!projectId && !!dataFileId,
      refetchOnMount: false,
    },
  )

  return (
    <section>
      {dataFileVersions?.pages.map((version, index) => (
        <SelectionCard
          ref={index === dataFileVersions.pages.length ? lastDataFileVersionRef : undefined}
          key={version.uuid}
          item={{
            title: version.name,
            createdAt: version.created_at,
            version: version.version,
            isWorkflowActive: version.is_active_version,
          }}
          state={version.version === values.version ? "selected" : "default"}
          onClick={() => {
            setFieldValue("version", version.version)

            // setting timeout to make sure the state changes before the refetch
            // didn't want to add another state to avoid bugs by having 2 sources of truths
            setTimeout(() => {
              refetchDataFile()
            }, 300)
          }}
        />
      ))}

      {(isFetchingNextPage || isVersionsFetching || isVersionsLoading) &&
        [1, 2, 3].map((item) => <SelectionLoaderCard key={item} />)}
    </section>
  )
}
